import React, { useMemo, useState } from 'react'
import {
  MetricCard,
  MetricCardGrid,
  METRIC_CARD_PREVIEW_STATE_LABELS,
  METRIC_CARD_PREVIEW_STATES,
  metricCardFromDashboard,
  metricCardFromMarketing,
  metricCardFromReportsKpi,
  type MetricCardDeltaTone,
  type MetricCardPreviewState,
  type MetricCardSize,
  type MetricCardTrend,
  type MetricCardValueTone,
  type MetricCardVariant,
} from '@enamelai/design-system'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './MetricCardShowcase.css'

const DASHBOARD_SAMPLE = {
  leftLabel: 'Scheduled',
  value: '42',
  valueOver: '48',
  rightLabel: 'Goal 48',
  footer: '6% vs yesterday',
  footerTrend: 'up' as const,
  showInfoIcon: true,
}

const REPORTS_SAMPLE = {
  id: 'active-patients',
  title: 'Active patients',
  value: '1,284',
  unitInline: 'pts',
  footer: '3% vs last month',
  tone: 'positive' as const,
}

const MARKETING_SAMPLES = [
  {
    label: 'Message sent',
    value: 1248,
    previous: 1104,
    percentage: 13,
    status: 'increase' as const,
    infoTooltip: 'Total outbound messages attempted in the campaign.',
  },
  {
    label: 'Delivered',
    value: 1180,
    previous: 1050,
    percentage: 12,
    status: 'increase' as const,
    infoTooltip: 'Messages successfully delivered to recipients.',
  },
  {
    label: 'Delivery rate',
    value: 94.5,
    previous: 92.1,
    percentage: 2,
    status: 'increase' as const,
    valueAsPercent: true,
    infoTooltip: 'Share of sent messages that were delivered.',
  },
  {
    label: 'Bounced',
    value: 68,
    previous: 54,
    percentage: 26,
    status: 'decrease' as const,
    infoTooltip: 'Messages that failed to reach recipients.',
  },
  {
    label: 'Replies',
    value: 214,
    previous: 198,
    percentage: 8,
    status: 'increase' as const,
    infoTooltip: 'Replies received from campaign recipients.',
  },
  {
    label: 'Scheduled',
    value: 42,
    previous: 55,
    percentage: 24,
    status: 'decrease' as const,
    infoTooltip: 'Messages queued for a future send time.',
  },
]

const MetricCardShowcase: React.FC = () => {
  const [label, setLabel] = useState('Production')
  const [value, setValue] = useState('$42,850')
  const [valueOver, setValueOver] = useState('')
  const [footer, setFooter] = useState('12% vs last week')
  const [showFooter, setShowFooter] = useState(true)
  const [unitInline, setUnitInline] = useState('')
  const [trend, setTrend] = useState<MetricCardTrend>('positive')
  const [valueTone, setValueTone] = useState<MetricCardValueTone>('default')
  const [variant, setVariant] = useState<MetricCardVariant>('card')
  const [size, setSize] = useState<MetricCardSize>('md')
  const [delta, setDelta] = useState('')
  const [deltaTone, setDeltaTone] = useState<MetricCardDeltaTone>('increase')
  const [showInfo, setShowInfo] = useState(false)
  const [showWarning, setShowWarning] = useState(false)
  const [highlightedState, setHighlightedState] =
    useState<MetricCardPreviewState>('default')

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'metric-card-highlight',
            label: 'Highlighted state',
            control: (
              <select
                id="metric-card-highlight"
                className="ui-field-control"
                value={highlightedState}
                onChange={(event) =>
                  setHighlightedState(
                    event.target.value as MetricCardPreviewState,
                  )
                }
              >
                {METRIC_CARD_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {METRIC_CARD_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'metric-card-label',
            label: 'Label',
            control: (
              <input
                id="metric-card-label"
                className="ui-field-control"
                type="text"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
              />
            ),
          },
          {
            id: 'metric-card-value',
            label: 'Value',
            control: (
              <input
                id="metric-card-value"
                className="ui-field-control"
                type="text"
                value={value}
                onChange={(event) => setValue(event.target.value)}
              />
            ),
          },
          {
            id: 'metric-card-value-over',
            label: 'Value over',
            control: (
              <input
                id="metric-card-value-over"
                className="ui-field-control"
                type="text"
                value={valueOver}
                placeholder="e.g. 48"
                onChange={(event) => setValueOver(event.target.value)}
              />
            ),
          },
          {
            id: 'metric-card-unit',
            label: 'Inline unit',
            control: (
              <input
                id="metric-card-unit"
                className="ui-field-control"
                type="text"
                value={unitInline}
                placeholder="e.g. visits"
                onChange={(event) => setUnitInline(event.target.value)}
              />
            ),
          },
          {
            id: 'metric-card-footer',
            label: 'Footer text',
            control: (
              <input
                id="metric-card-footer"
                className="ui-field-control"
                type="text"
                value={footer}
                disabled={!showFooter}
                onChange={(event) => setFooter(event.target.value)}
              />
            ),
          },
          {
            id: 'metric-card-show-footer',
            label: 'Footer',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="metric-card-show-footer"
                  type="checkbox"
                  checked={showFooter}
                  onChange={(event) => setShowFooter(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'metric-card-trend',
            label: 'Trend',
            control: (
              <select
                id="metric-card-trend"
                className="ui-field-control"
                value={trend}
                disabled={!showFooter}
                onChange={(event) =>
                  setTrend(event.target.value as MetricCardTrend)
                }
              >
                <option value="positive">Positive</option>
                <option value="negative">Negative</option>
                <option value="neutral">Neutral</option>
                <option value="muted">Muted</option>
              </select>
            ),
          },
          {
            id: 'metric-card-value-tone',
            label: 'Value tone',
            control: (
              <select
                id="metric-card-value-tone"
                className="ui-field-control"
                value={valueTone}
                onChange={(event) =>
                  setValueTone(event.target.value as MetricCardValueTone)
                }
              >
                <option value="default">Default</option>
                <option value="danger">Danger</option>
              </select>
            ),
          },
          {
            id: 'metric-card-variant',
            label: 'Variant',
            control: (
              <select
                id="metric-card-variant"
                className="ui-field-control"
                value={variant}
                onChange={(event) =>
                  setVariant(event.target.value as MetricCardVariant)
                }
              >
                <option value="card">Card (bordered)</option>
                <option value="plain">Plain (no border)</option>
              </select>
            ),
          },
          {
            id: 'metric-card-size',
            label: 'Size',
            control: (
              <select
                id="metric-card-size"
                className="ui-field-control"
                value={size}
                onChange={(event) =>
                  setSize(event.target.value as MetricCardSize)
                }
              >
                <option value="md">Medium (Dashboard / Reports)</option>
                <option value="lg">Large (Marketing)</option>
              </select>
            ),
          },
          {
            id: 'metric-card-delta',
            label: 'Delta %',
            control: (
              <input
                id="metric-card-delta"
                className="ui-field-control"
                type="text"
                value={delta}
                placeholder="e.g. 12"
                onChange={(event) => setDelta(event.target.value)}
              />
            ),
          },
          {
            id: 'metric-card-delta-tone',
            label: 'Delta tone',
            control: (
              <select
                id="metric-card-delta-tone"
                className="ui-field-control"
                value={deltaTone}
                disabled={!delta}
                onChange={(event) =>
                  setDeltaTone(event.target.value as MetricCardDeltaTone)
                }
              >
                <option value="increase">Increase</option>
                <option value="decrease">Decrease</option>
              </select>
            ),
          },
        ],
      },
      {
        title: 'State',
        fields: [
          {
            id: 'metric-card-info',
            label: 'Info icon',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="metric-card-info"
                  type="checkbox"
                  checked={showInfo}
                  onChange={(event) => setShowInfo(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'metric-card-warning',
            label: 'Warning icon',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="metric-card-warning"
                  type="checkbox"
                  checked={showWarning}
                  onChange={(event) => setShowWarning(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [
      delta,
      deltaTone,
      footer,
      highlightedState,
      label,
      showFooter,
      showInfo,
      showWarning,
      size,
      trend,
      unitInline,
      value,
      valueOver,
      valueTone,
      variant,
    ],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Metric card"
        description="Shared metric / KPI tile for Dashboard, Reports, and Marketing. Use size lg + delta badge for Campaign Overview."
        stageClassName="ui-preview__stage--wide metric-card-showcase__stage-wrap"
      >
        <div className={`metric-card-showcase__sheet`}>
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Property controls drive the first card when Highlighted state is
              Default. Other highlighted states show that preset in the live
              card.
            </p>
            <MetricCardGrid columns={3}>
              {highlightedState === 'default' ? (
                <MetricCard
                  label={label}
                  value={value}
                  valueOver={valueOver || undefined}
                  unitInline={unitInline || undefined}
                  footer={showFooter ? footer || undefined : undefined}
                  showFooter={showFooter}
                  trend={trend}
                  valueTone={valueTone}
                  variant={variant}
                  size={size}
                  delta={delta ? Number(delta) || delta : undefined}
                  deltaTone={deltaTone}
                  showInfoIcon={showInfo}
                  showWarningIcon={showWarning}
                />
              ) : (
                <MetricCard previewState={highlightedState} />
              )}
              <MetricCard
                label="New patients"
                value="128"
                unitInline="pts"
                footer="5% vs last month"
                trend="positive"
              />
              <MetricCard
                label="At risk"
                value="14"
                footer="Needs follow-up"
                trend="muted"
                valueTone="danger"
              />
            </MetricCardGrid>
          </section>

          <section className="metric-card-showcase__adapters">
            <h3 className="ui-showcase-live__title">Module adapters</h3>
            <p className="ui-showcase-live__note">
              Dashboard / Reports (md) and Marketing Campaign Overview (lg +
              delta).
            </p>
            <div className="metric-card-showcase__adapters-row">
              <MetricCard {...metricCardFromDashboard(DASHBOARD_SAMPLE)} />
              <MetricCard {...metricCardFromReportsKpi(REPORTS_SAMPLE)} />
            </div>
            <div className="metric-card-showcase__marketing-board">
              {MARKETING_SAMPLES.map((sample) => (
                <MetricCard
                  key={sample.label}
                  {...metricCardFromMarketing(sample)}
                />
              ))}
            </div>
          </section>

          <section className="metric-card-showcase__matrix">
            {METRIC_CARD_PREVIEW_STATES.map((previewState) => (
              <div
                key={previewState}
                className={`metric-card-showcase__row${
                  previewState === highlightedState
                    ? ' metric-card-showcase__row--selected'
                    : ''
                }`}
              >
                <h4 className="metric-card-showcase__label">
                  {METRIC_CARD_PREVIEW_STATE_LABELS[previewState]}
                </h4>
                <MetricCard previewState={previewState} />
              </div>
            ))}
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Metric card"
        sections={propertySections}
      />
    </>
  )
}

export default MetricCardShowcase
